import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useRef } from 'react';
import type { Swiper as SwiperClass } from 'swiper';
// import { UserIcon } from 'lucide-react';

import './productdata.scss';
import {
 ProductDetailProps,
 ProductDescription,
 ProductReview,
 ShippingDetail,
 ProductWarranty,
 ReturnPolicy,
} from '../../interface/interfaces';
import EmptyDisplay from '../EmptyDisplay/EmptyDisplay';

type Props = {
 details: ProductDetailProps | null;
};

const ProductData = ({ details }: Props) => {
 const [tabType, setTabType] = useState<string>('description');
 const tabOrder = [
  'description',
  'reviews',
  'shipping',
  'warranty',
  'return',
 ];
 const swiperRef = useRef<SwiperClass | null>(null);

 const [desc, setDesc] = useState<ProductDescription | null>(null);
 const [review, setReview] = useState<ProductReview | null>(null);
 const [shipping, setShipping] = useState<ShippingDetail | null>(
  null,
 );
 const [warranty, setWarranty] = useState<ProductWarranty | null>(
  null,
 );
 const [returnPolicy, setReturnPolicy] =
  useState<ReturnPolicy | null>(null);

 useEffect(() => {
  if (details) {
   setDesc(details.description);
   setReview(details.reviewDetails);
   setShipping(details.shippingDetails);
   setWarranty(details.warranty);
   setReturnPolicy(details.returnPolicy);
  }
 }, [details]);

 const handleTabSwitch = (type: string) => {
  setTabType(type);
  const index = tabOrder.indexOf(type);
  swiperRef.current?.slideTo(index);
 };

 const percFunc = (value: number, total: number) => {
  const percentage = (value / total) * 100 || 0;
  return percentage.toFixed(2);
 };

 return (
  <div className="productdata">
   <div className="menustrip">
    <div className="optGrid">
     <div
      className="eachopt"
      onClick={() => handleTabSwitch('description')}
     >
      <div className="txt">Description</div>

      {tabType === 'description' && (
       <motion.div
        layoutId="underline"
        className="underline"
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
       ></motion.div>
      )}
     </div>

     <div
      className="eachopt"
      onClick={() => handleTabSwitch('reviews')}
     >
      <div className="txt">Reviews</div>
      {tabType === 'reviews' && (
       <motion.div
        layoutId="underline"
        className="underline"
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
       ></motion.div>
      )}
     </div>

     {!details?.vendor.is_call_to_buy_enabled && (
      <div
       className="eachopt"
       onClick={() => handleTabSwitch('shipping')}
      >
       <div className="txt">Shipping</div>
       {tabType === 'shipping' && (
        <motion.div
         layoutId="underline"
         className="underline"
         transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        ></motion.div>
       )}
      </div>
     )}

     {!details?.vendor.is_call_to_buy_enabled && (
      <div
       className="eachopt"
       onClick={() => handleTabSwitch('warranty')}
      >
       <div className="txt">Warranty</div>
       {tabType === 'warranty' && (
        <motion.div
         layoutId="underline"
         className="underline"
         transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        ></motion.div>
       )}
      </div>
     )}

     {!details?.vendor.is_call_to_buy_enabled && (
      <div
       className="eachopt"
       onClick={() => handleTabSwitch('return')}
      >
       <div className="txt">Return policy</div>
       {tabType === 'return' && (
        <motion.div
         layoutId="underline"
         className="underline"
         transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        ></motion.div>
       )}
      </div>
     )}
    </div>
   </div>

   <div className="c_div">
    <Swiper
     onSwiper={(swiperInstance: SwiperClass) => {
      swiperRef.current = swiperInstance;
     }}
     onSlideChange={(swiper: SwiperClass) => {
      setTabType(tabOrder[swiper.activeIndex]);
     }}
     spaceBetween={0}
     slidesPerView={1}
     autoHeight={true}
    >
     <SwiperSlide>
      <div className="dataside desctab">
       {desc && (
        <>
         <div className="text">{desc.text}</div>

         {desc.features.length >= 1 && (
          <>
           <div className="title">Features:</div>

           <div className="ftTab">
            {desc.features.map((txt, id) => (
             <div
              className="eachFt"
              key={id}
             >
              <div className="dot"></div>
              <div className="txt">{txt}</div>
             </div>
            ))}
           </div>
          </>
         )}
        </>
       )}
      </div>
     </SwiperSlide>

     <SwiperSlide>
      <div className="dataside reviewtab">
       {review && (
        <>
         <div className="reviewGrid">
          <div className="starsideone">
           <div className="midbox">
            <div className="ratingNum">
             {review?.ratings.stars}/{review?.ratings.totalStars}
            </div>

            <div className="stars">
             {[...Array(review.ratings.stars)].map((_, i) => (
              <div
               className="svgicon"
               key={i}
              >
               <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
               >
                <path
                 d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
                 fill="#FFC453"
                 stroke="#FFC453"
                 stroke-width="1.83301"
                />
               </svg>
              </div>
             ))}

             {[
              ...Array(
               review.ratings.totalStars - review.ratings.stars,
              ),
             ].map((_, i) => (
              <div
               className="svgicon stripped"
               key={i}
              >
               <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
               >
                <path
                 d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
                 fill="#FFC453"
                 stroke="#FFC453"
                 stroke-width="1.83301"
                />
               </svg>
              </div>
             ))}
            </div>

            <div className="rvtext">
             ({review.totalReviews} review
             {review.totalReviews > 1 ? 's' : ''})
            </div>
           </div>
          </div>

          {review.starsNusers && (
           <div className="starsidetwo">
            {review.starsNusers.map((data, id) => (
             <div
              className="eachuserrating"
              key={id}
             >
              <div className="xcgrid">
               <div className="starnum">{data.stars}</div>
               <div className="svgicon">
                <svg
                 width="16"
                 height="16"
                 viewBox="0 0 16 16"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg"
                >
                 <path
                  d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
                  fill="#FFC453"
                  stroke="#FFC453"
                  stroke-width="1.83301"
                 />
                </svg>
               </div>
              </div>

              <div className="ratingbar">
               <div
                className="ratingprogress"
                style={{
                 width: `${percFunc(
                  data.numberOfUsers,
                  review.totalReviews,
                 )}%`,
                }}
               ></div>
              </div>
              <div className="useramt">{data.numberOfUsers}</div>
             </div>
            ))}
           </div>
          )}
         </div>

         <div className="title">Recent reviews</div>

         <div className="userreviewstab">
          {review.userReviews.map((data, id) => (
           <div
            className="eachreview"
            key={id}
           >
            <div
             className="userImg"
             style={{ backgroundImage: `url(${data.userImgUrl})` }}
            ></div>

            <div className="otherContent">
             <div className="upside">
              <div className="userdetail">
               <div className="userName">{data.userName}</div>

               <div className="useractivity">
                <span>Verified</span>
                <div className="smalldot"></div>
                <span>{data.timePurchased}</span>
               </div>
              </div>

              <div className="stars">
               {[...Array(data.starRatings.stars)].map((_, i) => (
                <div
                 className="svgicon"
                 key={i}
                >
                 <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                 >
                  <path
                   d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
                   fill="#FFC453"
                   stroke="#FFC453"
                   stroke-width="1.83301"
                  />
                 </svg>
                </div>
               ))}

               {[
                ...Array(
                 data.starRatings.totalStars - data.starRatings.stars,
                ),
               ].map((_, i) => (
                <div
                 className="svgicon stripped"
                 key={i}
                >
                 <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                 >
                  <path
                   d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
                   fill="#FFC453"
                   stroke="#FFC453"
                   stroke-width="1.83301"
                  />
                 </svg>
                </div>
               ))}
              </div>
             </div>

             <div className="comment">{data.comment}</div>
            </div>
           </div>
          ))}

          {review.userReviews.length <= 0 && (
           <EmptyDisplay
            text={'No reviews yet'}
            icon_class={''}
           ></EmptyDisplay>
          )}
         </div>
        </>
       )}
      </div>
     </SwiperSlide>

     <SwiperSlide>
      <div className="dataside shippingtab">
       {shipping && (
        <>
         <div className="bigtitle">Shipping Information:</div>

         <div
          className="dGrid"
          style={{ marginTop: '20px' }}
         >
          <div className="processingtime">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv processingtime"></div>
            </div>

            <div className="title">Processing time</div>
           </div>

           <div className="text">{shipping.processingTime}</div>
          </div>

          <div className="shippingoptions">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv shippingoptions"></div>
            </div>

            <div className="title">Shipping options</div>
           </div>

           <div className="text">
            You can choose the shipping method that is best for you
            from our list of delivery methods.
           </div>

           <div className="ftTab">
            {shipping.shippingOptions.map((txt, id) => (
             <div
              className="eachFt"
              key={id}
             >
              <div className="dot"></div>
              <div className="txt">{txt}</div>
             </div>
            ))}
           </div>

           <div className="text">
            <span style={{ fontWeight: '500' }}>Note:</span> Delivery
            times may vary during holidays or peak seasons
           </div>
          </div>

          <div className="ordertracking">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv ordertracking"></div>
            </div>

            <div className="title">Order tracking</div>
           </div>

           <div className="text">{shipping.orderTracking}</div>
          </div>

          <div className="needhelp">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv mail"></div>
            </div>

            <div className="title">Need Help?</div>
           </div>

           <div className="text">
            Email us at{' '}
            <span>
             <a href={`mailto:${shipping.supportEmail}`}>
              {shipping.supportEmail}
             </a>
            </span>{' '}
            or chat us live on weekdays from 9am - 6pm.
           </div>
          </div>
         </div>
        </>
       )}
      </div>
     </SwiperSlide>

     <SwiperSlide>
      <div className="dataside warrantytab">
       {warranty && (
        <>
         <div className="dGrid">
          <div className="processingtime">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv time"></div>
            </div>

            <div className="title">Processing time</div>
           </div>

           <div className="text">{warranty.processingTime}</div>
          </div>

          <div className="covered">
           <div className="titlebox">
            <div className="icn mark"></div>
            <div className="title">Covered</div>
           </div>

           <div className="ftTab">
            {warranty.coveredList.map((txt, id) => (
             <div
              className="eachFt"
              key={id}
             >
              <div className="dot"></div>
              <div className="txt">{txt}</div>
             </div>
            ))}
           </div>
          </div>

          <div className="notcovered">
           <div className="titlebox">
            <div className="icn x"></div>
            <div className="title">Not Covered</div>
           </div>

           <div className="ftTab">
            {warranty.notCoveredList.map((txt, id) => (
             <div
              className="eachFt"
              key={id}
             >
              <div className="dot"></div>
              <div className="txt">{txt}</div>
             </div>
            ))}
           </div>
          </div>

          <div className="needhelp">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv mail"></div>
            </div>

            <div className="title">Need Help?</div>
           </div>

           <div className="text">
            Email us at{' '}
            <span>
             <a href={`mailto:${warranty.supportEmail}`}>
              {warranty.supportEmail}
             </a>
            </span>{' '}
            or chat us live on weekdays from 9am - 6pm.
           </div>
          </div>
         </div>
        </>
       )}
      </div>
     </SwiperSlide>

     <SwiperSlide>
      <div className="dataside returnpolicy">
       {returnPolicy && (
        <>
         <div className="dGrid">
          <div className="text">
           If you’re not satisfied with your purchase, you can return
           items within 7- 14 days of delivery.
          </div>

          <div className="steps">
           <div className="titlebox">
            <div className="icon">
             <div className="imageDiv scroll"></div>
            </div>

            <div className="title">How to request return</div>
           </div>

           <div className="ftTab">
            {returnPolicy.steps.map((txt, id) => (
             <div
              className="eachFt"
              key={id}
             >
              <div className="txt">{id + 1}.</div>
              <div className="txt">{txt}</div>
             </div>
            ))}
           </div>
          </div>
         </div>
        </>
       )}
      </div>
     </SwiperSlide>
    </Swiper>
   </div>
  </div>
 );
};

export default ProductData;
